import React from "react";

import { Grid } from "@material-ui/core";
import { FieldArray, getIn, useFormikContext } from "formik";
import { uniqBy } from "lodash";
import { useSelector } from "react-redux";

import { SlimSecondaryButton } from "components/Button";
import SlimButton from "components/Button/SlimButton";
import { BreedField } from "components/Form/Fields/BreedField";
import { ProductSelectField } from "components/Form/Fields";
import {
  ControlledLifeCycleInput,
  Input,
  KilogramDisplayGramsInputProps,
  UnitPrice,
} from "components/Form/FormikControls";
import { FaIcon } from "components/AgGridButtonIcon/agGridButtonIcon";
import { Row } from "components/Layout";

import { PricingTypes } from "constants/pricingTypes";
import { SaleType } from "constants/sale";

import { formatDecimal } from "lib";

import { useIsMobile, useResolveSpeciesId } from "hooks";
import { getEffectiveDeploymentId } from "selectors";

import { faTrash, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { SectionDivider } from "components/ReviewCard";

import styled from "styled-components/macro";

export const IconButtonLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
`;

const addSaleLot = (list, saleLots) => {
  list.push({
    ...saleLots[saleLots.length - 1],
    quantity: "",
    total_mass_grams: "",
    unitPrice: "",
    id: undefined,
  });
};

interface SaleLotsContext {
  saleLots?: SaleLot[];
}

const LotCalculationSummary = ({ index }: { index: number }) => {
  const { values } = useFormikContext<SaleLotsContext>();
  const pricingTypeId = getIn(values, `saleLots[${index}].pricing_type_id`);
  const rawQuantity = getIn(values, `saleLots[${index}].quantity`);
  const quantity =
    rawQuantity !== "" && rawQuantity != null ? Number(rawQuantity) : null;
  const unitPrice = Number(getIn(values, `saleLots[${index}].unitPrice`)) || 0;
  const totalMassGrams =
    Number(getIn(values, `saleLots[${index}].total_mass_grams`)) || 0;

  switch (pricingTypeId) {
    case PricingTypes.PER_HEAD: {
      const total =
        quantity === null ? "N/A" : formatDecimal(quantity * unitPrice);
      return (
        <Grid item xs={12}>
          <Input
            label="Total"
            name={`saleLots[${index}]-summary-total`}
            overrideValue={total}
            beforeSymbol="$"
            disabled
            align="right"
          />
        </Grid>
      );
    }
    case PricingTypes.GROSS: {
      const average =
        quantity === null ? "N/A" : formatDecimal(unitPrice / quantity);
      return (
        <Grid item xs={12}>
          <Input
            label="Avg/Head"
            name={`saleLots[${index}]-summary-avg`}
            overrideValue={average}
            beforeSymbol="$"
            disabled
            align="right"
          />
        </Grid>
      );
    }
    case PricingTypes.PER_KILO: {
      const total = (unitPrice * totalMassGrams) / 100 / 1000;
      const averageWeight =
        quantity === null
          ? "N/A"
          : formatDecimal(totalMassGrams / 1000 / quantity);
      const average =
        quantity === null ? "N/A" : formatDecimal(total / quantity);
      return (
        <>
          <Grid item xs={4}>
            <Input
              label="Average kg"
              name={`saleLots[${index}]-summary-avgkg`}
              overrideValue={averageWeight}
              afterSymbol="kg"
              disabled
              align="right"
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label="Total"
              name={`saleLots[${index}]-summary-total`}
              overrideValue={formatDecimal(total)}
              beforeSymbol="$"
              disabled
              align="right"
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              label="Avg/Head"
              name={`saleLots[${index}]-summary-avg`}
              overrideValue={average}
              beforeSymbol="$"
              disabled
              align="right"
            />
          </Grid>
        </>
      );
    }
    default:
      return null;
  }
};

export interface MultiSaleLotsFormProps {
  addLineText?: string;
  handleClickDelete: (saleLot: SaleLot, index: number) => void;
  showQuickCode?: boolean;
  showCalculationSummary?: boolean;
  deploymentId?: number | null;
  selectedSpeciesId?: number | null;
  selectedSaleType?: SaleType | null;
}

export const MultiSaleLotsForm = ({
  addLineText = "Add line",
  handleClickDelete,
  showQuickCode = true,
  showCalculationSummary = false,
  deploymentId: deploymentIdProp = null,
  selectedSpeciesId = null,
  selectedSaleType = null,
}: MultiSaleLotsFormProps) => {
  const deploymentIdFromState = useSelector(getEffectiveDeploymentId);
  const deploymentId = deploymentIdProp || deploymentIdFromState;
  const speciesId = useResolveSpeciesId(selectedSpeciesId);
  const isMobile = useIsMobile();

  const { values } = useFormikContext<SaleLotsContext>();
  const { saleLots = [] } = values;

  const isPricingTypeDifferent = uniqBy(saleLots, "pricing_type_id").length > 1;

  const labelOrSpace = (index: number, label: string): string => {
    if (index === 0 || isMobile) {
      return label;
    }
    if (isPricingTypeDifferent) {
      return " ";
    }
    return "";
  };

  return (
    <FieldArray name="saleLots">
      {arrayHelpers => (
        <>
          {saleLots.map((saleLot, i) => (
            <React.Fragment key={saleLot.id || i}>
              {isMobile && (
                <SectionDivider>
                  Lot {saleLot?.lot_number || i + 1}
                </SectionDivider>
              )}

              <Grid item xs={4} md={1}>
                <Input
                  label={labelOrSpace(i, "Headcount")}
                  name={`saleLots[${i}]quantity`}
                  required={isMobile || i === 0}
                  type="number"
                />
              </Grid>
              <Grid item xs={4} md={2}>
                <ControlledLifeCycleInput
                  {...KilogramDisplayGramsInputProps}
                  label={labelOrSpace(i, "Total Weight")}
                  name={`saleLots[${i}]total_mass_grams`}
                />
              </Grid>
              <Grid item xs={4} md={2}>
                <UnitPrice
                  name={`saleLots[${i}]unitPrice`}
                  pricingTypeField={`saleLots[${i}]pricing_type_id`}
                  showLabel={i === 0 || isPricingTypeDifferent || isMobile}
                />
              </Grid>
              {showCalculationSummary && <LotCalculationSummary index={i} />}
              <Grid item xs={12} md={3}>
                <ProductSelectField
                  ns={`saleLots[${i}]`}
                  hideQuickSelect
                  deploymentId={deploymentId}
                  selectedSpeciesId={speciesId}
                  selectedSaleType={selectedSaleType}
                  bestMatchAutoSelect
                  label={labelOrSpace(i, "Description")}
                />
              </Grid>

              <Grid item xs={isMobile ? 12 : 10} md={3}>
                <BreedField
                  name={`saleLots[${i}]breed_id`}
                  label={labelOrSpace(i, "Breed")}
                  speciesId={speciesId}
                  deploymentId={deploymentId}
                  showQuickCode={showQuickCode}
                />
              </Grid>
              {(!isMobile || i >= 1) && (
                <Grid
                  item
                  xs={i === saleLots.length - 1 ? 6 : 12}
                  md={1}
                  style={{ alignSelf: "flex-end" }}
                >
                  <SlimSecondaryButton
                    type="button"
                    fullWidth
                    disabled={i === 0 && !saleLot.id}
                    onClick={() => handleClickDelete(saleLot as SaleLot, i)}
                  >
                    {isMobile ? (
                      <IconButtonLabel>
                        <FaIcon icon={faTrash} /> Delete lot
                      </IconButtonLabel>
                    ) : (
                      "Delete"
                    )}
                  </SlimSecondaryButton>
                </Grid>
              )}
              {isMobile && i === saleLots.length - 1 && (
                <Grid item xs={i === 0 ? 12 : 6}>
                  <SlimButton
                    fullWidth
                    onClick={() => addSaleLot(arrayHelpers, saleLots)}
                    type="button"
                  >
                    <IconButtonLabel>
                      <FaIcon icon={faPlus} /> {addLineText}
                    </IconButtonLabel>
                  </SlimButton>
                </Grid>
              )}
            </React.Fragment>
          ))}

          {!isMobile && (
            <Grid item xs={12}>
              <Row justifyEnd>
                <SlimButton
                  onClick={() => addSaleLot(arrayHelpers, saleLots)}
                  type="button"
                >
                  {addLineText}
                </SlimButton>
              </Row>
            </Grid>
          )}
        </>
      )}
    </FieldArray>
  );
};

export const removeFormikSaleLotAtIndex = <T,>(
  index: number,
  saleLots: T[] = [],
  defaultSaleLot: T,
  setFieldValue: (field: "saleLots", value: T[]) => void,
) => {
  const nextValue = saleLots.filter((_, i) => i !== index);
  const nextOrDefault = nextValue.length === 0 ? [defaultSaleLot] : nextValue;
  setFieldValue("saleLots", nextOrDefault);
};
